/**
 * WitnessedFirePanel — THE GAME.
 *
 * Play the governed execution lane. Tool-call requests approach; fire to permit
 * a witnessed call. An unwitnessed call has no permit round — firing produces no
 * projectile, so the call is structurally inexpressible at execution, not blocked
 * after the fact. The unwitnessed, forged calls stream past harmlessly: they were
 * never executable. That is PERMIT made playable.
 *
 * No game logic here: the panel owns the loop and rendering and dispatches
 * intents to the pure reducer in algorithms/witnessedFire. That reducer is the
 * single source of truth for which calls execute.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  ArcadeActionButton,
  ArcadeKeyframes,
  ArcadeStatPill,
  ComboBadge,
  FlashOverlay,
  useArcadeHotkeys,
  useArcadeScore,
  useFixedTimestep,
  useReducedMotion,
  type ArcadeHotkeyBinding,
} from "../../../games/index.js";
import wavesData from "../data/witnessedFireWaves.json" with { type: "json" };
import {
  createFireState,
  fireReducer,
  frontInLane,
  type FireState,
  type FireWave,
  type ToolDef,
} from "../algorithms/witnessedFire.js";

const WAVES = wavesData.waves as unknown as readonly [FireWave, ...FireWave[]];
const STEP_SECONDS = 1 / 30; // engine ticks ~30/s for an arcade pace
const C = {
  accent: "#34d399",
  witnessed: "#34d399",
  unwitnessed: "#f87171",
  amber: "#fbbf24",
  track: "rgba(148,163,184,0.25)",
  surface: "rgba(15,23,42,0.6)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  elev: "rgba(30,41,59,0.7)",
  bright: "#f8fafc",
};

function toolGlyph(wave: FireWave, toolId: string): string {
  return wave.tools.find((t: ToolDef) => t.id === toolId)?.glyph ?? "•";
}
function toolLabel(wave: FireWave, toolId: string): string {
  return wave.tools.find((t: ToolDef) => t.id === toolId)?.label ?? toolId;
}

export function WitnessedFirePanel(): React.JSX.Element {
  const reducedMotion = useReducedMotion();

  const [waveIndex, setWaveIndex] = useState(0);
  const wave = WAVES[waveIndex] ?? WAVES[0];

  const [state, setState] = useState<FireState>(() => createFireState(wave));
  const [running, setRunning] = useState(false);
  const arcade = useArcadeScore({
    hitColor: C.witnessed,
    missColor: C.unwitnessed,
  });

  const stateRef = useRef(state);
  stateRef.current = state;
  const isOver = state.status !== "playing";

  const onStep = useCallback((): void => {
    const prev = stateRef.current;
    const next = fireReducer(wave, prev, { kind: "tick" });
    if (next.breaches !== prev.breaches) arcade.miss();
    if (next !== prev) setState(next);
  }, [wave, arcade]);

  useFixedTimestep({
    running: running && !isOver,
    stepSeconds: STEP_SECONDS,
    onStep,
  });

  const fire = useCallback(
    (lane: number): void => {
      const prev = stateRef.current;
      if (prev.status !== "playing") return;
      if (!running) setRunning(true);
      const next = fireReducer(wave, prev, { kind: "fire", lane });
      setState(next);
      if (next.lastEvent === "permitted" && next.permitted !== prev.permitted) {
        arcade.hit();
      } else if (next.lastEvent === "no-shot") {
        // Firing on an unwitnessed front does nothing constructive: there is no
        // shot. Break the combo to teach that the round does not exist.
        arcade.miss();
      }
    },
    [running, wave, arcade],
  );

  const startWave = useCallback(
    (index: number): void => {
      const target = WAVES[index] ?? WAVES[0];
      setWaveIndex(index);
      setState(createFireState(target));
      setRunning(false);
      arcade.reset();
    },
    [arcade],
  );

  const reset = useCallback(() => {
    startWave(waveIndex);
  }, [waveIndex, startWave]);
  const nextWave = useCallback(() => {
    startWave(Math.min(WAVES.length - 1, waveIndex + 1));
  }, [waveIndex, startWave]);

  // Keyboard: number keys 1..lanes fire the corresponding lane.
  const fireHotkeys = useMemo(
    (): readonly ArcadeHotkeyBinding[] =>
      Array.from({ length: Math.min(9, wave.lanes) }, (_, index) => ({
        key: String(index + 1),
        enabled: !isOver,
        onTrigger: (): void => {
          fire(index);
        },
      })),
    [wave.lanes, isOver, fire],
  );
  useArcadeHotkeys(fireHotkeys);

  const rank = arcade.rank;
  const isLast = waveIndex === WAVES.length - 1;

  const statusMessage = useMemo((): string => {
    if (state.status === "cleared")
      return `Wave cleared — every witnessed call permitted. Rank ${rank}.`;
    if (state.status === "breached")
      return "Breach: a witnessed call reached execution unpermitted. Keep the governed lane covered.";
    if (!running)
      return "Fire a lane to start. Permit each witnessed call; the unwitnessed ones have no shot.";
    return "Fire to permit a witnessed call. An unwitnessed call has no permit round — it streams past.";
  }, [state.status, running, rank]);

  const lanes = Array.from({ length: wave.lanes }, (_, i) => i);

  return (
    <div className="hub-panel-surface p-6 space-y-4" style={{ color: C.text }}>
      <ArcadeKeyframes />
      <div>
        <h3
          style={{ color: C.accent, fontSize: 20, fontWeight: 800, margin: 0 }}
        >
          Witnessed Fire
        </h3>
        <p style={{ color: C.muted, fontSize: 13, margin: "4px 0 0" }}>
          Play · permit each witnessed tool call — an unwitnessed call has no
          shot
        </p>
      </div>

      <div
        style={{
          border: `1px solid ${C.accent}44`,
          background: C.surface,
          borderRadius: 12,
          padding: "12px 14px",
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        A tool call executes only when it carries a{" "}
        <strong style={{ color: C.bright }}>witnessed capability</strong>. Fire
        to permit a witnessed call before it reaches execution. An{" "}
        <strong style={{ color: C.bright }}>unwitnessed</strong> call has no
        permit round at all — firing produces no projectile, so the call is{" "}
        <strong style={{ color: C.bright }}>structurally inexpressible</strong>{" "}
        at execution, not blocked after the fact. The forged calls stream past
        harmlessly; they were never executable.
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {WAVES.map((candidate, index) => {
          const selected = index === waveIndex;
          return (
            <button
              key={candidate.id}
              type="button"
              onClick={() => {
                startWave(index);
              }}
              aria-pressed={selected}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                border: `1px solid ${selected ? C.accent : C.track}`,
                background: selected ? C.accent : C.elev,
                color: selected ? "#0b1120" : C.text,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {candidate.name}
            </button>
          );
        })}
      </div>
      <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>{wave.brief}</p>

      <div
        style={{
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <ArcadeStatPill label="Score" value={arcade.score} color={C.accent} />
        <ArcadeStatPill label="Rank" value={rank} color={C.amber} />
        <ArcadeStatPill
          label="Permitted"
          value={`${String(state.permitted)} / ${String(wave.quota)}`}
          color={C.witnessed}
        />
        <ArcadeStatPill
          label="Breaches"
          value={`${String(state.breaches)} / ${String(wave.breachBudget)}`}
          color={C.unwitnessed}
        />
        <ComboBadge
          combo={arcade.combo}
          color={C.accent}
          muted={C.muted}
          reducedMotion={reducedMotion}
        />
      </div>

      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {lanes.map((lane) => {
          const inLane = state.requests.filter((r) => r.lane === lane);
          const front = frontInLane(state, lane);
          const frontFirable = front?.witnessed ?? false;
          return (
            <div
              key={lane}
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <button
                type="button"
                onClick={() => {
                  fire(lane);
                }}
                disabled={isOver}
                aria-label={`Fire lane ${String(lane + 1)}`}
                style={{
                  flexShrink: 0,
                  width: 64,
                  padding: "8px 6px",
                  borderRadius: 10,
                  border: `1px solid ${frontFirable ? C.witnessed : C.track}`,
                  background: frontFirable ? `${C.witnessed}22` : C.elev,
                  color: frontFirable ? C.witnessed : C.muted,
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: isOver ? "default" : "pointer",
                }}
              >
                ▶ FIRE
                <span style={{ display: "block", fontSize: 9, opacity: 0.7 }}>
                  key {String(lane + 1)}
                </span>
              </button>
              <div
                role="list"
                aria-label={`Lane ${String(lane + 1)} requests`}
                style={{
                  position: "relative",
                  flex: 1,
                  height: 40,
                  borderRadius: 10,
                  background: C.surface,
                  border: `1px solid ${C.track}`,
                  overflow: "hidden",
                }}
              >
                {inLane.map((req) => {
                  const pct = 100 - (req.distance / wave.laneLength) * 100;
                  const color = req.witnessed ? C.witnessed : C.unwitnessed;
                  return (
                    <span
                      key={req.id}
                      role="listitem"
                      title={`${toolLabel(wave, req.toolId)} · ${req.witnessed ? "witnessed" : "unwitnessed"}`}
                      style={{
                        position: "absolute",
                        left: `${String(Math.max(0, Math.min(96, pct)))}%`,
                        top: "50%",
                        transform: "translate(-50%,-50%)",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        padding: "2px 7px",
                        borderRadius: 999,
                        border: `1px solid ${color}`,
                        background: `${color}1A`,
                        color,
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        transition: reducedMotion
                          ? "none"
                          : "left 120ms linear",
                      }}
                    >
                      <span aria-hidden>{toolGlyph(wave, req.toolId)}</span>
                      {req.witnessed ? "✓" : "⌀"}
                    </span>
                  );
                })}
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: C.amber,
                  }}
                />
              </div>
            </div>
          );
        })}
        <FlashOverlay
          token={arcade.flashToken}
          color={arcade.flashColor}
          reducedMotion={reducedMotion}
        />
      </div>

      <div
        role="status"
        aria-live="polite"
        style={{ color: C.text, fontSize: 13, minHeight: 20 }}
      >
        {statusMessage}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <ArcadeActionButton
          color={C.amber}
          onClick={reset}
          ariaLabel="Reset wave"
        >
          Reset wave
        </ArcadeActionButton>
        {state.status === "cleared" && !isLast ? (
          <ArcadeActionButton
            color={C.accent}
            onClick={nextWave}
            ariaLabel="Next wave"
          >
            Next wave
          </ArcadeActionButton>
        ) : null}
      </div>

      {state.status === "cleared" ? (
        <div
          style={{
            border: `1px solid ${C.witnessed}55`,
            background: `${C.witnessed}14`,
            borderRadius: 12,
            padding: "12px 14px",
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          Every witnessed call was permitted before execution, and no
          unwitnessed call ever executed — none could have, because firing an
          unwitnessed call has no permit round. The forbidden execution was
          structurally inexpressible by construction.
        </div>
      ) : null}

      {state.status === "breached" ? (
        <div
          style={{
            border: `1px solid ${C.unwitnessed}55`,
            background: `${C.unwitnessed}14`,
            borderRadius: 12,
            padding: "12px 14px",
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          A witnessed call reached execution without being permitted in time.
          The governed lane is what permits; keep every witnessed call covered.
          Unwitnessed calls were never the threat — they have no shot at all.
        </div>
      ) : null}
    </div>
  );
}
